import { IPlayerPosition, IPlayerStatus, ITeam } from '@/data/models'
import i18n from '@/plugins/i18n'

/** Default Player */
export interface IPlayer {
  id: number;
  first_name: string;
  gender: string;
  last_name: string;
  photo_url: string;
  birth_date: string;
  email: string;
  address: string;
  phone: string;
  club_history: string;

  team_id: number;
  player_position_id: number;
  player_status_id: number;

  team: ITeam;
  player_status: IPlayerStatus;
  player_position: IPlayerPosition;
}

/** Player paginated list **/
export interface IPlayerPaged {
  id: number;
  name: string;
  surname: string;
  totalPl: number;
  acwr: number;
  mood: number;
  fatigue: number;
  shape: number;
  teamId: number;
  positionId: number;
  playerStatusId: number;
  photoUrl: string;

  team: ITeam;
  playerStatus: IPlayerStatus;
  playerPosition: IPlayerPosition;
}

/** Player form **/
export interface IPlayerCreateForm {
  name: string;
  surname: string;
  dateOfBirth: string;
  nationalIdentificationNumber: string;
  birthCityAndCountry: string;
  address: string;
  phone: string;
  parents: string;
  school: string;
  teamId: number;
  sportDetails: string;
  registrationNumber: string;
  playerPositionId: number;
  playerStatusId: number;
  username: string;
  email: string;
  photo: File;
}

/**
 * Transform IPlayerCreateForm to FormData object
 *
 * @param playerFormData
 */
export const formDataFromPlayerForm = (playerFormData: IPlayerCreateForm): FormData => {
  const formData = new FormData()
  formData.append('name', playerFormData.name)
  formData.append('surname', playerFormData.surname)
  formData.append('dateOfBirth', playerFormData.dateOfBirth)
  formData.append('nationalIdentificationNumber', playerFormData.nationalIdentificationNumber)
  formData.append('birthCityAndCountry', playerFormData.birthCityAndCountry)
  formData.append('address', playerFormData.address)
  formData.append('phone', playerFormData.phone)
  formData.append('parents', playerFormData.parents)
  formData.append('school', playerFormData.school)
  if (playerFormData.teamId) formData.append('teamId', playerFormData.teamId.toString())
  formData.append('sportDetails', playerFormData.sportDetails)
  formData.append('registrationNumber', playerFormData.registrationNumber)
  if (playerFormData.playerPositionId) formData.append('playerPositionId', playerFormData.playerPositionId.toString())
  if (playerFormData.playerStatusId) formData.append('playerStatusId', playerFormData.playerStatusId.toString())
  formData.append('username', playerFormData.username)
  formData.append('email', playerFormData.email)
  if (playerFormData.photo) formData.append('photo', playerFormData.photo, playerFormData.photo.name)

  return formData
}

export enum MenstrualPhase {
  EARLY_FOLLICULAR = 'early_follicular',
  MID_FOLLICULAR = 'mid_follicular',
  LATE_FOLLICULAR = 'late_follicular',
  OVULATION = 'ovulation',
  EARLY_LUTEAL = 'early_luteal',
  MID_LUTEAL = 'mid_luteal',
  LATE_LUTEAL = 'late_luteal',
  PERIOD_OVERDUE = 'period_overdue',
  NO_PHASE = 'no_phase'
}

export const MenstrualPhases = {
  [MenstrualPhase.EARLY_FOLLICULAR]: {
    get text() { return i18n.t('menstrual_cycle.early_follicular') },
    description: '1 - 5',
    from: 1,
    to: 5,
    duration: 5,
    color: '#E03336'
  },
  [MenstrualPhase.MID_FOLLICULAR]: {
    get text() { return i18n.t('menstrual_cycle.mid_follicular') },
    description: '6 - 8',
    from: 6,
    to: 8,
    duration: 3,
    color: '#FD8A42'
  },
  [MenstrualPhase.LATE_FOLLICULAR]: {
    get text() { return i18n.t('menstrual_cycle.late_follicular') },
    description: '9 - 13',
    from: 9,
    to: 13,
    duration: 5,
    color: '#9d6a72'
  },
  [MenstrualPhase.OVULATION]: {
    get text() { return i18n.t('menstrual_cycle.ovulation') },
    description: '14',
    from: 14,
    to: 14,
    duration: 1,
    color: '#77ADF8'
  },
  [MenstrualPhase.EARLY_LUTEAL]: {
    get text() { return i18n.t('menstrual_cycle.early_luteal') },
    description: '15 - 20',
    from: 15,
    to: 20,
    duration: 6,
    color: '#60af9e'
  },
  [MenstrualPhase.MID_LUTEAL]: {
    get text() { return i18n.t('menstrual_cycle.mid_luteal') },
    description: '21 - 24',
    from: 21,
    to: 24,
    duration: 4,
    color: '#d0ad5f'
  },
  [MenstrualPhase.LATE_LUTEAL]: {
    get text() { return i18n.t('menstrual_cycle.late_luteal') },
    description: '25 - 31',
    from: 25,
    to: 31,
    duration: 7,
    color: '#d05f9b'
  },
  [MenstrualPhase.PERIOD_OVERDUE]: {
    get text() { return i18n.t('menstrual_cycle.period_overdue') },
    description: '31+',
    from: 31,
    to: null,
    duration: null,
    color: '#888888'
  }
}

export const musclesAndJoints = {
  head: { key: 'head', label: i18n.t('muscle_or_joint.head') },
  lff: { key: 'lff', label: i18n.t('muscle_or_joint.front_left_forearm') },
  rff: { key: 'rff', label: i18n.t('muscle_or_joint.front_right_forearm') },
  lfb: { key: 'lfb', label: i18n.t('muscle_or_joint.back_left_forearm') },
  rfb: { key: 'rfb', label: i18n.t('muscle_or_joint.back_right_forearm') },
  luaf: { key: 'luaf', label: i18n.t('muscle_or_joint.front_left_upper_arm') },
  ruaf: { key: 'ruaf', label: i18n.t('muscle_or_joint.front_right_upper_arm') },
  luab: { key: 'luab', label: i18n.t('muscle_or_joint.back_left_upper_arm') },
  ruab: { key: 'ruab', label: i18n.t('muscle_or_joint.back_right_upper_arm') },
  bl: { key: 'bl', label: i18n.t('muscle_or_joint.left_breast') },
  br: { key: 'br', label: i18n.t('muscle_or_joint.right_breast') },
  abdl: { key: 'abdl', label: i18n.t('muscle_or_joint.left_abdomen') },
  abdr: { key: 'abdr', label: i18n.t('muscle_or_joint.right_abdomen') },
  lutf: { key: 'lutf', label: i18n.t('muscle_or_joint.front_left_upper_thigh') },
  rutf: { key: 'rutf', label: i18n.t('muscle_or_joint.front_right_upper_thigh') },
  lutb: { key: 'lutb', label: i18n.t('muscle_or_joint.back_left_upper_thigh') },
  rutb: { key: 'rutb', label: i18n.t('muscle_or_joint.back_right_upper_thigh') },
  lllf: { key: 'lllf', label: i18n.t('muscle_or_joint.front_left_lower_leg') },
  rllf: { key: 'rllf', label: i18n.t('muscle_or_joint.front_right_lower_leg') },
  lllb: { key: 'lllb', label: i18n.t('muscle_or_joint.back_left_lower_leg') },
  rllb: { key: 'rllb', label: i18n.t('muscle_or_joint.back_right_lower_leg') },
  lbl: { key: 'lbl', label: i18n.t('muscle_or_joint.left_low_back') },
  lbr: { key: 'lbr', label: i18n.t('muscle_or_joint.right_low_back') },
  ubl: { key: 'ubl', label: i18n.t('muscle_or_joint.left_upper_back') },
  ubr: { key: 'ubr', label: i18n.t('muscle_or_joint.right_upper_back') },
  n: { key: 'n', label: i18n.t('muscle_or_joint.neck') },
  hr: { key: 'hr', label: i18n.t('muscle_or_joint.right_hand') },
  hl: { key: 'hl', label: i18n.t('muscle_or_joint.left_hand') },
  rw: { key: 'rw', label: i18n.t('muscle_or_joint.right_wrist') },
  lw: { key: 'lw', label: i18n.t('muscle_or_joint.left_wrist') },
  rel: { key: 'rel', label: i18n.t('muscle_or_joint.right_elbow') },
  lel: { key: 'lel', label: i18n.t('muscle_or_joint.left_elbow') },
  rsh: { key: 'rsh', label: i18n.t('muscle_or_joint.front_right_shoulder') },
  lsh: { key: 'lsh', label: i18n.t('muscle_or_joint.front_left_shoulder') },
  rh: { key: 'rh', label: i18n.t('muscle_or_joint.right_hip') },
  lh: { key: 'lh', label: i18n.t('muscle_or_joint.left_hip') },
  rg: { key: 'rg', label: i18n.t('muscle_or_joint.right_groin') },
  lg: { key: 'lg', label: i18n.t('muscle_or_joint.left_groin') },
  rk: { key: 'rk', label: i18n.t('muscle_or_joint.right_knee') },
  lk: { key: 'lk', label: i18n.t('muscle_or_joint.left_knee') },
  ran: { key: 'ran', label: i18n.t('muscle_or_joint.right_ankle') },
  lan: { key: 'lan', label: i18n.t('muscle_or_joint.left_ankle') },
  rft: { key: 'rft', label: i18n.t('muscle_or_joint.right_foot') },
  lft: { key: 'lft', label: i18n.t('muscle_or_joint.left_foot') },
  rat: { key: 'rat', label: i18n.t('muscle_or_joint.right_achilles_tendon') },
  lat: { key: 'lat', label: i18n.t('muscle_or_joint.left_achilles_tendon') },
  gl: { key: 'gl', label: i18n.t('muscle_or_joint.left_gluteus') },
  gr: { key: 'gr', label: i18n.t('muscle_or_joint.right_gluteus') },
  rbshm: { key: 'gr', label: i18n.t('muscle_or_joint.back_right_shoulder_muscle') },
  lbshm: { key: 'gr', label: i18n.t('muscle_or_joint.back_left_shoulder_muscle') },
  lshw: { key: 'gr', label: i18n.t('muscle_or_joint.left_shoulder_wrist') },
  rshw: { key: 'gr', label: i18n.t('muscle_or_joint.right_shoulder_wrist') },
  rrbm: { key: 'gr', label: i18n.t('muscle_or_joint.right_side_abdomen') },
  lrbm: { key: 'gr', label: i18n.t('muscle_or_joint.left_side_abdomen') },
  stn: { key: 'gr', label: i18n.t('muscle_or_joint.sternum') },
  rrb: { key: 'gr', label: i18n.t('muscle_or_joint.right_ribs') },
  lrb: { key: 'gr', label: i18n.t('muscle_or_joint.left_ribs') },
  cs: { key: 'gr', label: i18n.t('muscle_or_joint.spine_cervical') },
  ts: { key: 'gr', label: i18n.t('muscle_or_joint.spine_thoracic') },
  ls: { key: 'gr', label: i18n.t('muscle_or_joint.spine_lumbal') },
  scrplv: { key: 'gr', label: i18n.t('muscle_or_joint.pelvis') },
  shbl: { key: 'gr', label: i18n.t('muscle_or_joint.left_shoulder') },
  shbr: { key: 'gr', label: i18n.t('muscle_or_joint.right_shoulder_blade') }
}
